/**
 * SaveStatusIndicator for UI-Model-1
 *
 * Fixed bottom-right toast showing auto-save status:
 * - "Saving..." with spinner (blue) while SaveQueue is active
 * - "All changes saved" with checkmark (green) for 2s after save completes
 * - "Failed to save" with warning icon (red) for 3s on failure
 *
 * Only reacts to SaveQueue-based saves (auto-save).
 * ThemeEditor/CssEditor manual saves bypass SaveQueue and are excluded.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSaveQueue } from '../../contexts/SaveQueueContext';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const SaveStatusIndicator = () => {
  const { isSaving, hasPendingSaves, hasFailedSaves } = useSaveQueue();
  const [status, setStatus] = useState<SaveStatus>('idle');
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const wasSavingRef = useRef(false);

  useEffect(() => {
    // Clear any pending auto-dismiss timer when dependencies change
    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    // Priority 1: Actively saving
    if (isSaving || hasPendingSaves) {
      clearTimer();
      setStatus('saving');
      wasSavingRef.current = true;
      return;
    }

    // Priority 2: Failed saves (only when not actively saving)
    if (hasFailedSaves) {
      clearTimer();
      setStatus('error');
      timerRef.current = setTimeout(() => {
        setStatus('idle');
      }, 3000);
      return;
    }

    // Priority 3: Just finished saving successfully
    if (wasSavingRef.current) {
      wasSavingRef.current = false;
      clearTimer();
      setStatus('saved');
      timerRef.current = setTimeout(() => {
        setStatus('idle');
      }, 2000);
      return;
    }

    return () => clearTimer();
  }, [isSaving, hasPendingSaves, hasFailedSaves]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  if (status === 'idle') return null;

  return (
    <div className="tw-fixed tw-bottom-4 tw-right-4 tw-z-50" data-testid="save-status-indicator" role="status" aria-live="polite">
      <div
        className={`tw-px-4 tw-py-2 tw-rounded-md tw-shadow-lg tw-flex tw-items-center tw-space-x-2
          ${status === 'saving' ? 'tw-bg-tone-info-tint tw-text-tone-info-ink' : ''}
          ${status === 'saved' ? 'tw-bg-tone-success-tint tw-text-tone-success-ink' : ''}
          ${status === 'error' ? 'tw-bg-tone-danger-tint tw-text-tone-danger-ink' : ''}
        `}
      >
        {status === 'saving' && (
          <>
            <svg className="tw-animate-spin tw-h-4 tw-w-4" viewBox="0 0 24 24">
              <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span className="tw-text-sm">Saving...</span>
          </>
        )}

        {status === 'saved' && (
          <>
            <svg className="tw-h-4 tw-w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className="tw-text-sm">All changes saved</span>
          </>
        )}

        {status === 'error' && (
          <>
            <svg className="tw-h-4 tw-w-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="tw-text-sm">Failed to save</span>
          </>
        )}
      </div>
    </div>
  );
};

export default SaveStatusIndicator;
